import { AnimatePresence, motion } from "framer-motion";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Check } from "lucide-react";
import { QUESTIONS } from "@/data/quiz";
import { useState } from "react";

export default function Quiz({ answers, setAnswers, onComplete, onExit }) {
  const [index, setIndex] = useState(0);
  const q = QUESTIONS[index];
  const progress = (index / QUESTIONS.length) * 100;
  const selected = answers[q.id];

  const choose = (optIndex) => {
    setAnswers((prev) => ({ ...prev, [q.id]: optIndex }));
    setTimeout(() => {
      if (index === QUESTIONS.length - 1) onComplete();
      else setIndex((i) => i + 1);
    }, 280);
  };

  const back = () => {
    if (index === 0) onExit();
    else setIndex((i) => i - 1);
  };

  return (
    <div className="min-h-screen bg-white">
      <div className="fixed top-0 left-0 right-0 z-40">
        <Progress value={progress} className="h-1.5 rounded-none bg-slate-100 [&>div]:bg-brand" />
      </div>

      <div className="max-w-3xl mx-auto px-6 py-12 md:py-20">
        <div className="flex items-center justify-between mb-12">
          <Button
            data-testid="quiz-back-button"
            variant="ghost"
            onClick={back}
            className="text-slate-500 hover:text-slate-900 -ml-3"
          >
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          <div className="font-mono text-sm text-slate-400 tabular">
            {String(index + 1).padStart(2, "0")} / {String(QUESTIONS.length).padStart(2, "0")}
          </div>
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={q.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.4, ease: "easeOut" }}
          >
            <div className="text-xs tracking-[0.2em] uppercase font-bold text-brand mb-4">{q.category}</div>
            <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl tracking-tight font-bold text-slate-900 mb-12">
              {q.question}
            </h2>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {q.options.map((opt, i) => {
                const isSel = selected === i;
                return (
                  <button
                    key={i}
                    data-testid={`quiz-option-${q.id}-${i}`}
                    onClick={() => choose(i)}
                    className={`group text-left p-6 rounded-md border transition-[border-color,background-color,box-shadow] duration-200 flex items-start justify-between gap-4 ${
                      isSel
                        ? "border-brand bg-orange-50 ring-1 ring-brand"
                        : "border-slate-200 bg-white hover:border-brand hover:shadow-sm"
                    }`}
                  >
                    <span className="text-base font-medium text-slate-800">{opt.label}</span>
                    <span
                      className={`shrink-0 w-6 h-6 rounded-full border flex items-center justify-center transition-colors ${
                        isSel ? "bg-brand border-brand" : "border-slate-300 group-hover:border-brand"
                      }`}
                    >
                      {isSel && <Check className="w-4 h-4 text-white" strokeWidth={3} />}
                    </span>
                  </button>
                );
              })}
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
}
